'use client'

import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cva, VariantProps } from 'class-variance-authority'
import { ComponentProps } from 'react'

import { cn } from '@/shared/lib'

const colorVariants = cva('', {
  variants: {
    color: {
      white: 'bg-white fill-white text-black',
      black: 'bg-gray-900 fill-black text-white',
      purple: 'bg-purple-500 fill-purple-500 text-white',
      blue: 'bg-blue-600 fill-blue-600 text-white',
      red: 'bg-red-500 fill-red-500 text-white',
    },
  },
})

export const Provider = ({
  delayDuration = 0,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Provider>) => {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

export const Root = ({
  ...props
}: ComponentProps<typeof TooltipPrimitive.Root>) => {
  return (
    <Provider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </Provider>
  )
}

export const Trigger = ({
  ...props
}: ComponentProps<typeof TooltipPrimitive.Trigger>) => {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

type ContentProps = ComponentProps<typeof TooltipPrimitive.Content> &
  VariantProps<typeof colorVariants> & {
    arrow?: boolean
  }

export const Content = ({
  color = 'white',
  side = 'top',
  sideOffset = 0,
  align = 'center',
  alignOffset = 0,
  arrow = true,
  arrowPadding = 0,
  className,
  children,
  ...props
}: ContentProps) => {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        className={cn(
          'typo-caption1 z-tooltip w-fit rounded-md p-2 font-medium text-balance',
          'drop-shadow-[0px_0px_4px_rgba(0,0,0,0.12),2px_6px_12px_rgba(0,0,0,0.12)] filter',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          'data-[side=bottom]:slide-in-from-top-2',
          'data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2',
          'data-[side=top]:slide-in-from-bottom-2',
          colorVariants({ color }),
          className,
        )}
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        arrowPadding={arrowPadding}
        {...props}
      >
        {children}
        {arrow && (
          <TooltipPrimitive.Arrow
            className={cn(
              'z-popover size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 shadow-[0_-3px_12px_rgba(0,0,0,0.12))]',
              colorVariants({ color }),
            )}
          />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}
